// useTioLayer.ts - Tomorrow.io tile layer management with crossfade transitions
import { useEffect, useRef } from 'react';
import type * as mapboxgl from 'mapbox-gl';
import { getTioTileUrlTemplate, type TioTileField } from '../../api/tomorrowIoTilesService';
import type { EventWithCircuit } from '../../api/eventsService';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';

interface UseTioLayerParams {
  mapRef: React.RefObject<mapboxgl.Map | null>;
  enabled: boolean;

  tioSourceId: string;
  tioLayerId: string;
  tioSourceId2: string;
  tioLayerId2: string;

  tioActiveLayerRef: React.RefObject<'layer1' | 'layer2'>;

  mapTileProvider: MapTileProvider;
  tioTimestamps: string[];
  tioCurrentIndex: number;
  tioTileField: TioTileField;
  selectedEvent: EventWithCircuit | null;
  radarOpacity: number;
  styleVersion: number;

  /** Optional preload tracking set. When provided, next-frame tiles are preloaded. */
  tioTilePreloadRef?: React.RefObject<Set<string>>;
}

const MAX_RETRIES = 20;
const RETRY_DELAY = 50;
const CROSSFADE_DURATION = 300;
const CROSSFADE_FALLBACK_MS = 350;

/**
 * Reusable hook for Tomorrow.io raster tile layer with crossfade transitions.
 *
 * Each map instance (main, split, PiP) calls this with its own IDs and refs.
 * The hook manages A/B double-buffered raster sources, crossfade animation,
 * and cleanup when switching away from a TIO provider.
 */
export function useTioLayer({
  mapRef,
  enabled,
  tioSourceId,
  tioLayerId,
  tioSourceId2,
  tioLayerId2,
  tioActiveLayerRef,
  mapTileProvider,
  tioTimestamps,
  tioCurrentIndex,
  tioTileField,
  selectedEvent,
  radarOpacity,
  styleVersion,
  tioTilePreloadRef,
}: UseTioLayerParams): void {
  const crossfadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Main frame update effect
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent || !enabled || !isTioProvider(mapTileProvider)) {
      // Cleanup when TIO is not active
      if (map) {
        try {
          if (map.getLayer(tioLayerId)) map.removeLayer(tioLayerId);
          if (map.getSource(tioSourceId)) map.removeSource(tioSourceId);
          if (map.getLayer(tioLayerId2)) map.removeLayer(tioLayerId2);
          if (map.getSource(tioSourceId2)) map.removeSource(tioSourceId2);
        } catch {
          // Ignore errors during cleanup
        }
      }
      tioActiveLayerRef.current = 'layer1';
      return;
    }

    if (tioTimestamps.length === 0) {
      try {
        if (map.getLayer(tioLayerId)) map.removeLayer(tioLayerId);
        if (map.getSource(tioSourceId)) map.removeSource(tioSourceId);
        if (map.getLayer(tioLayerId2)) map.removeLayer(tioLayerId2);
        if (map.getSource(tioSourceId2)) map.removeSource(tioSourceId2);
      } catch {
        // Ignore
      }
      tioActiveLayerRef.current = 'layer1';
      return;
    }

    const currentTimestamp = tioTimestamps[tioCurrentIndex];
    if (!currentTimestamp) return;

    // Cancel any in-progress crossfade immediately and ensure clean state
    if (crossfadeTimeoutRef.current) {
      clearTimeout(crossfadeTimeoutRef.current);
      crossfadeTimeoutRef.current = null;

      const currentActive = tioActiveLayerRef.current;
      const curId = currentActive === 'layer1' ? tioLayerId : tioLayerId2;
      const othId = currentActive === 'layer1' ? tioLayerId2 : tioLayerId;
      if (map.getLayer(curId)) {
        map.setPaintProperty(curId, 'raster-opacity', radarOpacity);
      }
      if (map.getLayer(othId)) {
        map.setPaintProperty(othId, 'raster-opacity', 0);
      }
    }

    const excludedLayers = mapTileProvider === 'tomorrow.io-global' ? 'FIA' : undefined;

    let retryCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let dataListener: ((e: mapboxgl.MapDataEvent) => void) | null = null;
    let idleListener: (() => void) | null = null;
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const preloadNextFrame = () => {
      if (!tioTilePreloadRef) return;
      if (tioCurrentIndex < tioTimestamps.length - 1) {
        const nextTimestamp = tioTimestamps[tioCurrentIndex + 1];
        const nextTileUrl = getTioTileUrlTemplate(tioTileField, nextTimestamp, 'png', excludedLayers);

        if (!tioTilePreloadRef.current.has(nextTileUrl)) {
          tioTilePreloadRef.current.add(nextTileUrl);

          const bounds = map.getBounds();
          if (!bounds) return;

          const zoom = Math.floor(map.getZoom());
          const minZoom = Math.max(6, zoom - 1);
          const maxZoom = Math.min(12, zoom + 1);

          for (let z = minZoom; z <= maxZoom; z++) {
            const nw = map.project(bounds.getNorthWest());
            const se = map.project(bounds.getSouthEast());
            const minX = Math.floor((nw.x / 256) / Math.pow(2, z - zoom));
            const maxX = Math.ceil((se.x / 256) / Math.pow(2, z - zoom));
            const minY = Math.floor((nw.y / 256) / Math.pow(2, z - zoom));
            const maxY = Math.ceil((se.y / 256) / Math.pow(2, z - zoom));
            const maxTiles = 9;
            let tileCount = 0;

            for (let x = Math.max(0, minX - 1); x <= Math.min(Math.pow(2, z) - 1, maxX + 1) && tileCount < maxTiles; x++) {
              for (let y = Math.max(0, minY - 1); y <= Math.min(Math.pow(2, z) - 1, maxY + 1) && tileCount < maxTiles; y++) {
                const tileUrl = nextTileUrl.replace('{z}', z.toString()).replace('{x}', x.toString()).replace('{y}', y.toString());
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = tileUrl;
                tileCount++;
              }
            }
          }
        }
      }
    };

    function updateTioLayer() {
      if (!map || !currentTimestamp) return;

      if (!map.isStyleLoaded()) {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          timeoutId = setTimeout(updateTioLayer, RETRY_DELAY);
        }
        return;
      }

      const tileUrl = getTioTileUrlTemplate(tioTileField, currentTimestamp, 'png', excludedLayers);

      const activeLayer = tioActiveLayerRef.current;
      const nextLayer = activeLayer === 'layer1' ? 'layer2' : 'layer1';
      const nextSourceId = nextLayer === 'layer1' ? tioSourceId : tioSourceId2;
      const nextLayerId = nextLayer === 'layer1' ? tioLayerId : tioLayerId2;
      const currentLayerId = activeLayer === 'layer1' ? tioLayerId : tioLayerId2;

      const layer1Exists = map.getLayer(tioLayerId);
      const layer2Exists = map.getLayer(tioLayerId2);
      const nextSourceExists = map.getSource(nextSourceId);
      const nextLayerExists = map.getLayer(nextLayerId);

      // Initialize on first load
      if (!layer1Exists && !layer2Exists) {
        if (!map.getSource(tioSourceId)) {
          map.addSource(tioSourceId, {
            type: 'raster',
            tiles: [tileUrl],
            tileSize: 256,
            minzoom: 6,
            maxzoom: 12,
            attribution: '© Tomorrow.io',
          });
        }
        map.addLayer({
          id: tioLayerId,
          type: 'raster',
          source: tioSourceId,
          paint: { 'raster-opacity': radarOpacity, 'raster-fade-duration': CROSSFADE_DURATION },
          layout: { visibility: 'visible' },
        });
        tioActiveLayerRef.current = 'layer1';
        preloadNextFrame();
        return;
      }

      // Update inactive layer with new tiles
      if (nextSourceExists) {
        const source = map.getSource(nextSourceId) as mapboxgl.RasterTileSource;
        source.setTiles([tileUrl]);
      } else {
        map.addSource(nextSourceId, {
          type: 'raster',
          tiles: [tileUrl],
          tileSize: 256,
          minzoom: 6,
          maxzoom: 12,
          attribution: '© Tomorrow.io',
        });
      }

      if (!nextLayerExists) {
        map.addLayer({
          id: nextLayerId,
          type: 'raster',
          source: nextSourceId,
          paint: { 'raster-opacity': 0, 'raster-fade-duration': CROSSFADE_DURATION },
          layout: { visibility: 'visible' },
        });
      } else {
        map.setPaintProperty(nextLayerId, 'raster-opacity', 0);
        map.setPaintProperty(nextLayerId, 'raster-fade-duration', CROSSFADE_DURATION);
      }

      if (map.getLayer(nextLayerId) && map.getLayer(currentLayerId)) {
        map.moveLayer(nextLayerId);
      }

      const startCrossfade = () => {
        if (map.getLayer(nextLayerId)) {
          map.setPaintProperty(nextLayerId, 'raster-fade-duration', CROSSFADE_DURATION);
          map.setPaintProperty(nextLayerId, 'raster-opacity', radarOpacity);
        }

        crossfadeTimeoutRef.current = setTimeout(() => {
          if (map.getLayer(currentLayerId)) {
            map.setPaintProperty(currentLayerId, 'raster-fade-duration', 0);
            map.setPaintProperty(currentLayerId, 'raster-opacity', 0);
          }
          tioActiveLayerRef.current = nextLayer;
          crossfadeTimeoutRef.current = null;
        }, CROSSFADE_DURATION + 50);
      };

      // Clean up stale listeners
      const source = map.getSource(nextSourceId) as mapboxgl.RasterTileSource;
      if (dataListener && source) source.off('data', dataListener);
      if (idleListener && map) map.off('idle', idleListener);
      if (crossfadeTimeoutRef.current) {
        clearTimeout(crossfadeTimeoutRef.current);
        crossfadeTimeoutRef.current = null;
      }

      let crossfadeStarted = false;
      const triggerCrossfade = () => {
        if (crossfadeStarted) return;
        crossfadeStarted = true;

        if (dataListener && source) { source.off('data', dataListener); dataListener = null; }
        if (idleListener && map) { map.off('idle', idleListener); idleListener = null; }
        if (fallbackTimeoutId) { clearTimeout(fallbackTimeoutId); fallbackTimeoutId = null; }

        startCrossfade();
      };

      if (source) {
        let tilesStartedLoading = false;
        dataListener = (e: mapboxgl.MapDataEvent) => {
          if (!tilesStartedLoading && 'dataType' in e && e.dataType === 'source') {
            tilesStartedLoading = true;
            if (map.isSourceLoaded(nextSourceId)) {
              setTimeout(triggerCrossfade, 100);
            } else {
              setTimeout(triggerCrossfade, 250);
            }
          }
        };
        source.on('data', dataListener);
      }

      idleListener = () => { triggerCrossfade(); };
      map.once('idle', idleListener);

      fallbackTimeoutId = setTimeout(() => { triggerCrossfade(); }, CROSSFADE_FALLBACK_MS);

      preloadNextFrame();
    }

    updateTioLayer();

    const mapForCleanup = map;
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
      if (crossfadeTimeoutRef.current) {
        clearTimeout(crossfadeTimeoutRef.current);
        crossfadeTimeoutRef.current = null;
      }
      try {
        if (mapForCleanup) {
          if (idleListener) mapForCleanup.off('idle', idleListener);
          const s1 = mapForCleanup.getSource(tioSourceId) as mapboxgl.RasterTileSource | undefined;
          const s2 = mapForCleanup.getSource(tioSourceId2) as mapboxgl.RasterTileSource | undefined;
          if (dataListener) {
            if (s1) s1.off('data', dataListener);
            if (s2) s2.off('data', dataListener);
          }
        }
      } catch {
        // Ignore errors during cleanup
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- radarOpacity intentionally excluded; handled by separate opacity effect
  }, [mapRef, enabled, mapTileProvider, tioTimestamps, tioCurrentIndex, tioTileField, selectedEvent, styleVersion, tioSourceId, tioLayerId, tioSourceId2, tioLayerId2, tioActiveLayerRef, tioTilePreloadRef]);

  // Separate opacity effect for slider changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !enabled) return;

    const activeLayerId = tioActiveLayerRef.current === 'layer1' ? tioLayerId : tioLayerId2;
    if (map.getLayer(activeLayerId)) {
      map.setPaintProperty(activeLayerId, 'raster-opacity', radarOpacity);
    }
  }, [radarOpacity, mapRef, enabled, tioLayerId, tioLayerId2, tioActiveLayerRef]);
}
